import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiArrowRight,
  FiCheck,
  FiCopy,
  FiEdit3,
  FiGrid,
  FiLock,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiShield,
  FiUser,
  FiUsers,
  FiZap
} = FiIcons;

const emptyForm = {
  app_slug: '',
  app_name: '',
  is_active: true,
  setup_scope: ''
};

const Apps = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copiedAppId, setCopiedAppId] = useState('');
  const [showTechnical, setShowTechnical] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const result = await apiCall(() => pb.collection('apps').getFullList({ sort: 'app_name' }));
      setApps(result);
    } catch (error) {
      console.error('Failed to load apps:', error);
      setApps([]);
      const status = Number(error?.status || error?.response?.status || 0);
      setLoadError(
        status === 403 || status === 404
          ? 'PocketBase is connected, but this account cannot read Apps. Import/update the authenticated collection API rules.'
          : 'Could not load Apps. Confirm the PocketBase connection and admin access.'
      );
    } finally {
      setLoading(false);
    }
  };

  const slugify = (text) => String(text || '')
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const validateSlug = (slug) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  const clearFieldError = (field) => {
    if (!formErrors[field]) return;
    setFormErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.app_name.trim()) errors.app_name = 'App name is required';
    if (!formData.app_slug.trim()) {
      errors.app_slug = 'App slug is required';
    } else if (!validateSlug(formData.app_slug.trim())) {
      errors.app_slug = 'Use lowercase letters, numbers and hyphens only';
    }
    if (!editingApp && !formData.setup_scope) {
      errors.setup_scope = 'Choose whether the App is primarily client-facing or internal';
    }

    setFormErrors(errors);
    if (errors.app_slug) setShowTechnical(true);
    if (Object.keys(errors).length > 0) {
      setSubmitError('Complete the highlighted field(s) before continuing.');
      return false;
    }
    return true;
  };

  const extractPocketBaseFieldErrors = (error) => {
    const raw = error?.data?.data || error?.response?.data || {};
    const next = {};
    Object.entries(raw).forEach(([field, fieldError]) => {
      const message = fieldError?.message || (Array.isArray(fieldError) ? fieldError[0] : fieldError);
      if (message) next[field] = String(message);
    });
    if (next.app_slug && /unique|already|exist/i.test(next.app_slug)) {
      next.app_slug = 'This app slug is already in use. Choose a different slug.';
    }
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitError('');
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        app_slug: formData.app_slug.trim(),
        app_name: formData.app_name.trim(),
        is_active: Boolean(formData.is_active)
      };

      if (editingApp) {
        await apiCall(() => pb.collection('apps').update(editingApp.id, payload));
        await loadApps();
        closeForm();
      } else {
        const setupScope = formData.setup_scope;
        const createdApp = await apiCall(() => pb.collection('apps').create(payload));

        // Scope persistence is additive and deliberately non-blocking so an older
        // PocketBase schema can still create Apps. Once the optional App scope
        // field is imported, this becomes the durable App-level default.
        try {
          await pb.collection('apps').update(createdApp.id, { default_visibility_scope: setupScope });
        } catch (scopeError) {
          console.warn('App scope field is not available yet; using route fallback.', scopeError?.status || '');
        }

        closeForm();
        navigate(`/apps/${createdApp.id}/setup?scope=${encodeURIComponent(setupScope)}`);
      }
    } catch (error) {
      console.error('Could not save App:', error);
      const nextErrors = extractPocketBaseFieldErrors(error);
      const fieldNames = Object.keys(nextErrors);
      const status = Number(error?.status || error?.response?.status || 0);

      if (nextErrors.app_slug) setShowTechnical(true);
      setFormErrors(nextErrors);

      if (fieldNames.length > 0) {
        setSubmitError('PocketBase rejected one or more values. Check the highlighted field(s) and try again.');
      } else if (status === 403 || status === 404) {
        setSubmitError('Your App details are valid, but PocketBase denied the save. The Apps collection needs authenticated API access rules before this action can succeed.');
      } else if (status === 0) {
        setSubmitError('Could not reach PocketBase. Check the backend URL or network connection and try again.');
      } else {
        setSubmitError(error?.message || 'PocketBase could not save this App. No individual form field was reported as invalid.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingApp(null);
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError('');
    setSubmitting(false);
    setShowTechnical(false);
    setSlugEdited(false);
  };

  const openNewForm = () => {
    setEditingApp(null);
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError('');
    setSubmitting(false);
    setShowTechnical(false);
    setSlugEdited(false);
    setShowForm(true);
  };

  const handleEdit = (app) => {
    setEditingApp(app);
    setFormData({
      app_slug: app.app_slug || '',
      app_name: app.app_name || '',
      is_active: app.is_active !== false,
      setup_scope: app.default_visibility_scope || ''
    });
    setFormErrors({});
    setSubmitError('');
    setSubmitting(false);
    setShowTechnical(true);
    setSlugEdited(true);
    setShowForm(true);
  };

  const handleToggleActive = async (app) => {
    const nextActive = !app.is_active;
    setLoadError('');
    setApps(current => current.map(item => item.id === app.id ? { ...item, is_active: nextActive } : item));

    try {
      await apiCall(() => pb.collection('apps').update(app.id, { is_active: nextActive }));
    } catch (error) {
      console.error('Failed to toggle app status:', error);
      setApps(current => current.map(item => item.id === app.id ? { ...item, is_active: app.is_active } : item));
      const status = Number(error?.status || error?.response?.status || 0);
      setLoadError(
        status === 403 || status === 404
          ? 'PocketBase denied this update. Confirm authenticated API rules for the Apps collection.'
          : 'Could not change this App status. Please try again.'
      );
    }
  };

  const handleAppNameChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      app_name: value,
      app_slug: !editingApp && !slugEdited ? slugify(value) : prev.app_slug
    }));
    clearFieldError('app_name');
    if (!slugEdited && slugify(value)) clearFieldError('app_slug');
  };

  const handleScopeChange = (scope) => {
    setFormData(prev => ({ ...prev, setup_scope: scope }));
    clearFieldError('setup_scope');
  };

  const handleSlugChange = (event) => {
    const value = event.target.value;
    setSlugEdited(true);
    setFormData(prev => ({ ...prev, app_slug: value }));
    if (value && validateSlug(value)) clearFieldError('app_slug');
  };

  const handleGenerateSlug = () => {
    const slug = slugify(formData.app_name);
    setSlugEdited(false);
    setFormData(prev => ({ ...prev, app_slug: slug }));
    if (slug) clearFieldError('app_slug');
  };

  const getTrainingLibraryUrl = (app) => {
    if (!app?.training_library_key) return '';
    return `${window.location.origin}/training/${encodeURIComponent(app.training_library_key)}`;
  };

  const handleCopyTrainingRoute = async (app) => {
    const url = getTrainingLibraryUrl(app);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedAppId(app.id);
      window.setTimeout(() => setCopiedAppId(''), 1800);
    } catch (error) {
      console.error('Could not copy training route:', error);
      setLoadError('Could not copy the training route. Please copy it manually.');
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredApps = apps.filter(app => {
    if (!normalizedSearch) return true;
    return String(app.app_slug || '').toLowerCase().includes(normalizedSearch) ||
      String(app.app_name || '').toLowerCase().includes(normalizedSearch);
  });

  const baseInputClass = 'w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-500';
  const inputClass = (field, extra = '') => `${baseInputClass} ${formErrors[field] ? 'app-form-control-error' : ''} ${extra}`.trim();
  const labelClass = 'block text-sm font-medium text-slate-300 mb-1.5';

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Apps Management</h1>
          <p className="text-slate-400 mt-1">Create an App once, then manage its videos, topics and integration from one workspace.</p>
        </div>
        <button type="button" onClick={openNewForm} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-lg shadow-blue-900/20 whitespace-nowrap">
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add App</span>
        </button>
      </div>

      <div className="mb-6 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
        <SafeIcon icon={FiZap} className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-700">Minimal-entry workflow</p>
          <p className="text-sm text-slate-400 mt-1">Create the App with its name and intended audience. Video Hub generates the slug, starts it active, and sends you to Smart Setup where future content can reuse the latest App settings automatically.</p>
        </div>
      </div>

      {loadError && (
        <div className="mb-6 form-error-banner px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="mb-6">
        <div className="relative max-w-md">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
          <input type="text" placeholder="Search Apps..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} className="pl-10 pr-4 py-2.5 w-full bg-slate-900 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-400" />
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">App</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Training Library</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-900/50">
              {loading ? (
                [...Array(4)].map((_, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4"><div className="h-4 w-36 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-7 w-40 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-16 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-8 w-24 rounded bg-slate-800 animate-pulse ml-auto" /></td>
                  </tr>
                ))
              ) : filteredApps.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <SafeIcon icon={FiGrid} className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 font-medium">No Apps found</p>
                    <p className="text-slate-600 text-sm mt-1">Create an App and Video Hub will guide the remaining setup.</p>
                  </td>
                </tr>
              ) : filteredApps.map(app => {
                const trainingUrl = getTrainingLibraryUrl(app);
                return (
                  <tr key={app.id} className="align-top">
                    <td className="px-6 py-4 min-w-[240px]">
                      <button type="button" onClick={() => navigate(`/apps/${app.id}/setup`)} className="text-left group">
                        <span className="text-sm font-semibold text-white group-hover:text-blue-600">{app.app_name}</span>
                        <span className="block text-xs text-slate-500 font-mono mt-1">{app.app_slug}</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 min-w-[300px]">
                      {trainingUrl ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-600 border border-green-500/20">
                              <SafeIcon icon={FiShield} className="w-3.5 h-3.5" /> Route key available
                            </span>
                            <button type="button" onClick={() => handleCopyTrainingRoute(app)} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-500/10" title="Copy Training Library route">
                              <SafeIcon icon={copiedAppId === app.id ? FiCheck : FiCopy} className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-slate-600 mt-2 font-mono truncate max-w-[420px]">{trainingUrl}</p>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <SafeIcon icon={FiLock} className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-700">Backend setup pending</p>
                            <p className="text-xs text-slate-500 mt-1">Normal App/video management is available once collection access rules are enabled.</p>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-badge ${app.is_active ? 'status-badge-active' : 'status-badge-inactive'}`}>
                        {app.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button type="button" onClick={() => navigate(`/apps/${app.id}/setup`)} className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-500/10" title="Open Smart Setup"><SafeIcon icon={FiArrowRight} className="w-4 h-4" /></button>
                        <button type="button" onClick={() => handleEdit(app)} className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-500/10" title="Edit App"><SafeIcon icon={FiEdit3} className="w-4 h-4" /></button>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={Boolean(app.is_active)}
                          aria-label={`${app.is_active ? 'Deactivate' : 'Activate'} ${app.app_name}`}
                          onClick={() => handleToggleActive(app)}
                          className="app-state-switch"
                          title={app.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                        >
                          <span className="sr-only">{app.is_active ? 'Active' : 'Inactive'}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 max-w-lg w-full overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 mb-2"><SafeIcon icon={FiZap} className="w-3.5 h-3.5" /> Guided setup</div>
                  <h3 className="text-xl font-bold text-white">{editingApp ? 'Edit App' : 'Create a new App'}</h3>
                  <p className="text-sm text-slate-500 mt-1">{editingApp ? 'Update the App record.' : 'Enter the essentials only. Technical values are generated automatically.'}</p>
                </div>
                <SafeIcon icon={FiGrid} className="w-5 h-5 text-slate-600" />
              </div>

              {submitError && (
                <div className="mb-4 form-error-banner px-4 py-3 rounded-xl flex items-start gap-2 text-sm" role="alert">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div>
                  <label className={labelClass}>App Name *</label>
                  <input type="text" value={formData.app_name} onChange={handleAppNameChange} className={inputClass('app_name')} placeholder="e.g. ScopeShield" autoFocus aria-invalid={Boolean(formErrors.app_name)} />
                  {formErrors.app_name && <p className="app-field-error-text text-sm mt-1">{formErrors.app_name}</p>}
                  {!editingApp && formData.app_slug && !showTechnical && <p className="text-xs text-slate-500 mt-2">Identifier generated automatically: <span className="font-mono text-slate-400">{formData.app_slug}</span></p>}
                </div>

                {!editingApp && (
                  <div>
                    <label className={labelClass}>Who is the content primarily for? *</label>
                    <div className={formErrors.setup_scope ? 'app-field-error-group' : ''} aria-invalid={Boolean(formErrors.setup_scope)}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => handleScopeChange('client_facing')}
                          aria-pressed={formData.setup_scope === 'client_facing'}
                          className={`scope-choice p-4 rounded-xl text-left ${formData.setup_scope === 'client_facing' ? 'scope-choice-client-selected' : ''}`}
                        >
                          {formData.setup_scope === 'client_facing' && <span className="scope-choice-check"><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /></span>}
                          <SafeIcon icon={FiUsers} className="scope-choice-icon w-5 h-5 text-blue-600 mb-2" />
                          <p className="text-sm font-semibold text-white">Client Facing</p>
                          <p className="text-xs text-slate-500 mt-1 pr-4">Users of the client App</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleScopeChange('bpas_internal_team')}
                          aria-pressed={formData.setup_scope === 'bpas_internal_team'}
                          className={`scope-choice p-4 rounded-xl text-left ${formData.setup_scope === 'bpas_internal_team' ? 'scope-choice-internal-selected' : ''}`}
                        >
                          {formData.setup_scope === 'bpas_internal_team' && <span className="scope-choice-check"><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /></span>}
                          <SafeIcon icon={FiUser} className="scope-choice-icon w-5 h-5 text-purple-500 mb-2" />
                          <p className="text-sm font-semibold text-white">Internal Team</p>
                          <p className="text-xs text-slate-500 mt-1 pr-4">BPA internal guidance</p>
                        </button>
                      </div>
                    </div>
                    {formErrors.setup_scope && <p className="app-field-error-text text-sm mt-1">{formErrors.setup_scope}</p>}
                  </div>
                )}

                <button type="button" onClick={() => setShowTechnical(current => !current)} className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-blue-600">
                  <SafeIcon icon={FiSettings} className="w-4 h-4" />
                  {showTechnical ? 'Hide technical settings' : 'Show technical settings'}
                </button>

                {showTechnical && (
                  <div className="space-y-4 p-4 rounded-xl bg-slate-950 border border-slate-800">
                    <div>
                      <label className={labelClass}>App Slug *</label>
                      <div className="relative">
                        <input type="text" value={formData.app_slug} onChange={handleSlugChange} className={inputClass('app_slug', 'pr-12')} placeholder="e.g. scope-shield" aria-invalid={Boolean(formErrors.app_slug)} />
                        <button type="button" onClick={handleGenerateSlug} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-500/10 rounded" title="Regenerate from App Name"><SafeIcon icon={FiRefreshCw} className="w-4 h-4" /></button>
                      </div>
                      {formErrors.app_slug && <p className="app-field-error-text text-sm mt-1">{formErrors.app_slug}</p>}
                    </div>

                    <div className="flex items-center justify-between gap-4 rounded-xl bg-white/70 border border-slate-800 p-3">
                      <div>
                        <p className="text-sm font-semibold text-white">App status</p>
                        <p className="text-xs text-slate-500 mt-0.5">{formData.is_active ? 'Active and available for content management' : 'Inactive and visually marked as off'}</p>
                      </div>
                      <button type="button" role="switch" aria-checked={Boolean(formData.is_active)} onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))} className="app-state-switch" aria-label="Toggle App active status" />
                    </div>
                  </div>
                )}

                {!editingApp && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-3 flex items-start gap-2">
                    <SafeIcon icon={FiShield} className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-slate-500">After creation you will go directly to the App workspace. The selected scope becomes the default for this App when the App scope field is available; later videos continue to reuse the latest saved content settings.</p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={closeForm} disabled={submitting} className="px-4 py-2 text-slate-300 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50">Cancel</button>
                  <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 flex items-center gap-2 disabled:opacity-60">
                    {submitting ? 'Saving…' : editingApp ? 'Update App' : <><span>Create & Continue</span><SafeIcon icon={FiArrowRight} className="w-4 h-4" /></>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Apps;